import { arrayx } from "@deathbycode/fp-ts-ext"
import { joinWithSlash, splitOnSlash } from "app/common/fp-ts/array"
import { FacetValue } from "app/search/codecs/map"
import { array, option, predicate, record, string } from "fp-ts"
import { pipe } from "fp-ts/function"

type UnflattenForestArgs<I, O, K extends string> = {
  addToParent: (parent: O, child: O) => void
  getKey: (t: I) => K
  getParentKey: (i: I) => option.Option<K>
  isRootNode: predicate.Predicate<[key: K, value: O]>
  toNode: (t: I) => O
}

const unflattenForest =
  <I, O, K extends string = string>({
    addToParent,
    getKey,
    getParentKey,
    isRootNode,
    toNode,
  }: UnflattenForestArgs<I, O, K>) =>
  (arr: Array<I>): Array<O> => {
    return pipe(
      arr,
      array.reduce({} as Record<K, O>, (acc: Record<K, O>, item: I) => {
        const newNode = toNode(item)
        acc[getKey(item)] = newNode

        pipe(
          getParentKey(item),
          option.chain(option.fromPredicate(predicate.not(string.isEmpty))),
          option.map((parentKey) => {
            const parent = acc[parentKey]

            if (parent) {
              addToParent(parent, newNode)
            }
          })
        )

        return acc
      }),
      record.toEntries,
      array.filter(isRootNode),
      array.map(([, values]) => values)
    )
  }

type FacetNode = {
  children: FacetNode[]
  count: number
  name: string
  value: string
}

export const unflattenFacetResults = unflattenForest<FacetValue, FacetNode>({
  addToParent: (p, c) => {
    p.children.push(c)
  },
  getKey: (f) => f.value,
  getParentKey: (f) => pipe(f.value, splitOnSlash, array.init, option.map(joinWithSlash)),
  isRootNode: ([key]) => pipe(key, splitOnSlash, arrayx.isSingle),
  toNode: (f) => ({
    children: [],
    count: f.count ?? 0,
    name: pipe(
      f.name,
      splitOnSlash,
      array.last,
      option.getOrElse(() => "")
    ),
    value: f.value,
  }),
})
